import React from 'react';

import type { NextPage } from 'next';
import { useRouter } from 'next/router';

import { CheckIcon } from '@heroicons/react/outline';
import { NextSeo } from 'next-seo';

import {
  BottomGraphic,
  FadeIn,
  Footer,
  GradientBg,
  Header,
  InsuranceCarousel,
  InsuranceTabs,
  PageHeader
} from '../components';
import { SmallBusinessSvg } from '../components/svg';
import { smallBusiness } from '../constants/insuranceTypes';

const reasons = [
  {
    name: 'Requirements',
    description: 'Lender or lease requirements'
  },
  {
    name: 'Accidents',
    description: 'Employees accidents and injuries'
  },
  {
    name: 'Loss',
    description: 'Lost, stolen, or damaged property and inventory'
  },
  {
    name: 'Lawsuit',
    description: 'Reputational damage and customer lawsuit'
  },
  {
    name: 'Interruption',
    description: 'Business interruption and lost income'
  }
];

const types = Object.keys(smallBusiness).map(type => ({
  title: type,
  icon: smallBusiness[type]
}));

const SmallBusiness: NextPage = () => {
  const router = useRouter();
  const canonicalUrl = (
    `https://www.coverdash.com` + (router.asPath === '/' ? '' : router.asPath)
  ).split('?')[0];
  return (
    <>
      <NextSeo
        title="Small Business"
        description="Small business insurance for your industry: GL, BOP, workers comp, and more. See who we cover and get an affordable quote online in minutes. — Coverdash"
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title: 'Small Business',
          description:
            'Small business insurance for your industry: GL, BOP, workers comp, and more. See who we cover and get an affordable quote online in minutes. — Coverdash'
        }}
      />
      <div className="overflow-hidden">
        <GradientBg />
        <Header />

        {/* Hero */}
        <PageHeader
          title="Small business owner"
          description="Running your own business comes with plenty of achievements, but every business also has its own risks"
          image={
            <SmallBusinessSvg className="mx-auto max-w-2xl w-full mt-8 md:mt-0" />
          }
        />

        {/* Section-1 */}
        <div className="max-w-7xl mx-auto py-8 lg:py-20 px-4 sm:px-6 lg:px-8 lg:grid lg:grid-cols-5 lg:gap-x-8">
          <div className="lg:col-span-2 text-center lg:text-left">
            <FadeIn>
              <p className="text-base font-semibold text-primary uppercase tracking-wide">
                Everything you need
              </p>
            </FadeIn>
            <FadeIn>
              <h2 className="mt-2 text-5xl font-extrabold text-alt">
                Why do{' '}
                <span className="text-primary">small business owners</span> need
                insurance?
              </h2>
            </FadeIn>

            <p className="mt-4 text-lg text-gray-500">
              Small businesses are often left in the dark when it comes to
              protecting what they have built. No matter what type of small
              business you operate, each one comes with a particular set of
              common risks. Obtaining the right insurance is crucial to protect
              your business from:
            </p>
          </div>
          <div className="mt-12 lg:mt-0 lg:col-span-3">
            <dl className="space-y-10 sm:space-y-0 sm:grid sm:grid-cols-2 sm:grid-rows-3 sm:grid-flow-col sm:gap-x-6 sm:gap-y-10 lg:gap-x-8">
              {reasons.map(reason => (
                <div key={reason.name} className="relative">
                  <dt>
                    <CheckIcon
                      className="absolute h-6 w-6 text-green-500"
                      aria-hidden="true"
                    />
                    <p className="ml-9 text-lg leading-6 font-medium text-gray-900">
                      {reason.name}
                    </p>
                  </dt>
                  <dd className="mt-2 ml-9 text-base text-gray-500">
                    {reason.description}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        {/* Section-2 */}
        <div className="max-w-7xl mx-auto py-8 lg:py-20 px-4 sm:px-6 lg:pg-8 text-center">
          <FadeIn>
            <h2 className="max-w-5xl mx-auto mt-2 text-5xl font-extrabold text-alt">
              What does{' '}
              <span className="text-primary">small business owners</span>{' '}
              insurance cover?{' '}
            </h2>
          </FadeIn>
          <div className="max-w-2xl mx-auto text-xl text-gray-500">
            <p className="mt-6">
              Choosing the right small business insurance can help protect you
              from costly risks and liabilities, the right policies for your
              business will depend what product you sell and what service you
              offer and where you are located.
            </p>
            <p className="mt-4">
              Here are some of the most popular insurance policies for small
              business, covering the most common risks you&apos;ll face as a
              small business owner:
            </p>
          </div>
          <div className="mt-12">
            <InsuranceTabs />
          </div>
        </div>

        {/* Section-3 */}
        <div className="mx-auto max-w-3xl lg:max-w-7xl px-4 sm:px-6 lg:px-8 py-8 lg:py-20 text-center">
          <FadeIn>
            <h2 className="max-w-5xl mx-auto mt-2 text-5xl font-extrabold text-alt">
              What types of{' '}
              <span className="text-primary">small business owners</span> do we
              cover?
            </h2>
          </FadeIn>
          <div className="mt-12 px-2 md:px-12 sm:px-0">
            <InsuranceCarousel items={types} />
          </div>
        </div>

        {/* Section-4 */}
        <div className="mx-auto max-w-3xl lg:max-w-7xl py-8 lg:py-20 px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <FadeIn>
                <h2 className="mt-2 text-5xl font-extrabold text-alt text-center sm:text-left">
                  How much does{' '}
                  <span className="text-primary">
                    small business owners insurance
                  </span>{' '}
                  cost?
                </h2>
              </FadeIn>
              <div className="mt-4 text-lg text-gray-500 text-center sm:text-left">
                <p>
                  Coverdash helps you identify the appropriate coverage to suit
                  your small business needs without breaking the bank. The exact
                  amount you pay will depend on:
                </p>

                <ul className="list-disc p-5 text-left">
                  <li>The type of small business you operate</li>
                  <li>Your revenue and employee payroll</li>
                  <li>Your number of employees</li>
                  <li>Your business location</li>
                </ul>
              </div>
            </div>
            <div className="justify-right items-right">
              <img
                src="/img/insurance-cost.jpg"
                className="h-full w-full lg:w-[140%]"
                alt="illustration of woman pointing at infographic"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        <BottomGraphic />
        <Footer />
      </div>
    </>
  );
};

export default SmallBusiness;
